import React, { useState } from 'react';
import { TopicChip } from '../topic-chip/TopicChip';
import { useLiveAPIContext } from '../../contexts/LiveAPIContext';
import AudioPulse1 from '../int-pulse/AudioPulse1';
import './explainer-picker.scss';

const DEPARTMENTS = [
  { emoji: '🍰', label: 'Production' },
  { emoji: '🎨', label: 'Design' },
  { emoji: '👥', label: 'HR' },
  { emoji: '💼', label: 'Business' },
  { emoji: '🛒', label: 'Purchase' },
];

const PRODUCTION_RECIPES = [
  { emoji: '🍓', label: 'CHOCOLATE & BERRY CAKE' },
  { emoji: '🐰', label: 'EASTER OVERLOAD CHOCOLATE CAKE' },
  { emoji: '🍫', label: 'CHOCO MOUSSE' },
];

// Recipe instructions content
const RECIPE_INSTRUCTIONS = {
  'CHOCOLATE & BERRY CAKE': `step 1 - 40g blueberry filling
step 2 - 40g strawberry filling
step 3 - Mix well
step 4 -Belgium Truffle Mousse: 300g Belgium Truffle +75g Whipped cream
~
Layering and Finishing

step 5 -75g Sponge + 20g Sugar syrup + 50g Belgium Truffle + 20g mixed berry filling

step 6 - 2nd layer: 75g sponge+20g sugar syrup + 50g Belgium Truffle +20g mixed berry filling

step 7 - last layer: 75g sponge + 20g sugar syrup
step 8 -cover the cake with 100g Belgium truffle

step 9 -make a chocolate band using 40g dark compound
step 10 - make a drop design (T16 Nozzle ) using 40g Belgium mousse
step 11- spread 25g mixed berry filling on the top center`,

  'EASTER OVERLOAD CHOCOLATE CAKE': `STEP 1- Mix 100g chocolate truffle with 15g whipped cream, to make a choco mousse

cake finishing
step 2 - cover the chocolate truffle cake entirely with choco mousse
step 3 - use a scale or palette knife to make this log design on the side of the cake
step 4- use a scale or palette knife to make this spiral design on the cake
step 5- spread cake crumbs on the middle and 4 sides of the cake
step 6 - for the garnish, we will use 4 pcs (20g) fondant carrot, 5 pcs (15g) chocolate rabbits, 4 big (25g each) and 4 small (5g each ) fondant eggs`,

  'CHOCO MOUSSE': `Generic chocolate mousse instructions`
};

const ExplainerPicker: React.FC = () => {
  const [step, setStep] = useState(1);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<string | null>(null);
  
  const { client, connect, connected, volume, disconnect } = useLiveAPIContext();

  const handleNext = () => setStep((prev) => prev + 1);
  const handleBack = () => setStep((prev) => prev - 1);

  const handleSelection = (type: 'department' | 'recipe', label: string) => {
    if (type === 'department') setSelectedDepartment(label);
    else if (type === 'recipe') setSelectedRecipe(label);
  };

  const handleReset = () => {
    disconnect();
    setStep(1);
    setSelectedDepartment(null);
    setSelectedRecipe(null);
  };

  const handleStart = async () => {
    if (selectedDepartment && selectedRecipe && !connected) {
      try {
        await connect();
        
        // Get recipe instructions based on selected recipe
        const recipeInstructions = RECIPE_INSTRUCTIONS[selectedRecipe as keyof typeof RECIPE_INSTRUCTIONS] || '';
        
        client.send([
          {
            text: `Explain how to make ${selectedRecipe}.
            You're EaseLearnAI's AI Trainer.
            First give a brief intro about yourself then ask them which language - hindi, english, kannada or tamil , wait for language response then start explaining the recipe.
            You're created by EaseLearnAI and you're here to help trainees learn.
            You are made by EaseLearn AI to help staff learn better. If someone asks for personal information, politely decline and redirect them to the recipe.
            If someone asks for model used or any other technical details, just tell you're made by EaseLearn AI and are here to help with training.
            Keep your explanation focused on the recipe steps.
            
            Here are the specific recipe steps to explain also explain each steps in detail:
            ${recipeInstructions}`,
          },
        ]);
      } catch (error) {
        console.error("Failed to start streaming:", error);
      }
    }
  };

  return (
    <div className="explainer-picker">
      <h1 className="title">EaseLearn AI Beta</h1>

      {connected ? (
        <div className="step-container">
          <AudioPulse1 active={connected} volume={volume} />
          <h2 className="text-center mt-4 text-xl">Explanation in Progress...</h2>
          <button className="button" onClick={handleReset} >End Session</button>
        </div>
      ) : (
        <div>
          {step === 1 && (
            <div className="step-container">
              <h2>Select Department:</h2>
              <div className="chips-container">
                {DEPARTMENTS.map((dept) => (
                  <TopicChip key={dept.label} {...dept} isSelected={selectedDepartment === dept.label} onClick={() => handleSelection('department', dept.label)} />
                ))}
              </div>
              <button onClick={handleNext} disabled={!selectedDepartment} className='button'>Next</button>
            </div>
          )}

          {step === 2 && (
            <div className="step-container">
              <h2>Select Recipe:</h2>
              <div className="chips-container">
                {selectedDepartment === 'Production' && 
                  PRODUCTION_RECIPES.map((recipe) => (
                    <TopicChip key={recipe.label} {...recipe} isSelected={selectedRecipe === recipe.label} onClick={() => handleSelection('recipe', recipe.label)} />
                  ))
                }
                {/* Add more department-specific options as needed */}
              </div>
              <div className="button-container">
                <button onClick={handleBack} className='button'>Back</button>
                <button onClick={handleStart} disabled={!selectedRecipe} className='button'>Start Talking</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExplainerPicker;
